import * as XLSX from 'xlsx';

export type DetectedFormat = 'job_seeker' | 'business_contact' | 'mixed' | 'unknown';
export type OutreachPersonKind = 'job_seeker' | 'business_contact' | 'recruiter' | 'unknown';

export interface ParsedOutreachRow {
  full_name: string | null;
  company_name: string | null;
  person_kind: OutreachPersonKind;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;
  gender: string | null;
  address: string | null;
  functional_area: string | null;
  specialization: string | null;
  industry: string | null;
  current_salary: string | null;
  seniority_level: string | null;
  location: string | null;
  current_role: string | null;
  skills: string[];
  source_row_status: string | null;
  source_followup_date: string | null;
  attributes: Record<string, string>;
}

export interface ParsedSheet {
  sheetName: string;
  detectedFormat: DetectedFormat;
  columnMapping: Record<string, string>;
  headers: string[];
  rows: ParsedOutreachRow[];
}

export interface ParsedImportFile {
  filename: string;
  fileFormat: 'xlsx' | 'xls' | 'csv';
  sheets: ParsedSheet[];
}

/** Normalized field → accepted header variants (lowercase). */
const FIELD_ALIASES: Record<string, string[]> = {
  full_name: [
    'name', 'full name', 'full_name', 'contact person', 'contact_person', 'contact',
  ],
  company_name: ['customer name', 'customer_name', 'company', 'organization', 'org'],
  email: ['email id', 'email_id', 'email', 'e-mail', 'mail'],
  phone: ['mobile no.', 'mobile no', 'mobile', 'phone', 'contact no', 'contact number'],
  date_of_birth: ['date of birth', 'dob', 'birth date', 'birthdate'],
  gender: ['gender', 'sex'],
  address: ['address', 'addr'],
  functional_area: ['functional area', 'functional_area', 'function'],
  specialization: ['area of specialization', 'specialization', 'specialisation', 'speciality'],
  industry: ['industry', 'sector'],
  current_salary: ['current salary', 'salary', 'ctc', 'current ctc'],
  seniority_level: ['level', 'seniority', 'grade'],
  location: ['current location', 'location', 'city', 'place'],
  current_role: ['role', 'designation', 'title', 'current role', 'job title'],
  source_row_status: ['status'],
  source_followup_date: ['followup date', 'followup_date', 'follow up date', 'follow-up date'],
  skills: ['skills', 'skill set', 'key skills'],
};

const BUSINESS_HEADERS = new Set(['customer name', 'customer_name', 'contact person', 'contact_person']);
const JOB_SEEKER_HEADERS = new Set(['functional area', 'functional_area', 'area of specialization', 'name']);

function normHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, ' ');
}

function cellStr(v: unknown): string {
  if (v == null) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v).trim();
}

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let cur = '';
  let inQ = false;
  let fields: string[] = [];
  const src = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    const next = src[i + 1];
    if (ch === '"') {
      if (inQ && next === '"') { cur += '"'; i++; } else { inQ = !inQ; }
    } else if (ch === ',' && !inQ) {
      fields.push(cur); cur = '';
    } else if ((ch === '\n' || ch === '\r') && !inQ) {
      fields.push(cur); cur = '';
      if (fields.some(f => f)) rows.push(fields);
      fields = [];
      if (ch === '\r' && next === '\n') i++;
    } else {
      cur += ch;
    }
  }
  if (cur || fields.length) {
    fields.push(cur);
    if (fields.some(f => f)) rows.push(fields);
  }
  return rows;
}

function looksLikePhone(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  const digits = v.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 13 && /^[\d\s\-+().]+$/.test(v);
}

function looksLikeEmail(value: string): boolean {
  return value.includes('@');
}

function isPhoneHeader(n: string): boolean {
  return FIELD_ALIASES.phone.some((a) => n === a || n.includes('mobile') || n.includes('phone'));
}

function isEmailHeader(n: string): boolean {
  return FIELD_ALIASES.email.some((a) => n === a || n.includes('email') || n.includes('e-mail'));
}

function isPersonNameHeader(n: string): boolean {
  if (!n) return false;
  if (n === 'name' || n === 'contact person' || n === 'contact_person' || n === 'candidate name') {
    return true;
  }
  if (n.includes('customer') || n.includes('company') || n.includes('organization')) return false;
  if (n.endsWith(' name') || n.startsWith('name ')) return true;
  return false;
}

function isCompanyHeader(n: string): boolean {
  return FIELD_ALIASES.company_name.some((a) => n === a) || n.includes('customer name');
}

/** Pick the row that looks like column headers (skip title rows above the table). */
function findHeaderRowIndex(grid: string[][]): number {
  const maxScan = Math.min(15, grid.length);
  let bestIdx = 0;
  let bestScore = 0;
  for (let i = 0; i < maxScan; i++) {
    let score = 0;
    for (const h of grid[i] ?? []) {
      const n = normHeader(h);
      if (!n) continue;
      for (const aliases of Object.values(FIELD_ALIASES)) {
        if (aliases.includes(n)) score += 2;
      }
      if (BUSINESS_HEADERS.has(n)) score += 3;
      if (JOB_SEEKER_HEADERS.has(n)) score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }
  return bestScore >= 3 ? bestIdx : 0;
}

function buildHeaderMap(headers: string[]): Record<string, number> {
  const map: Record<string, number> = {};
  headers.forEach((h, i) => {
    const n = normHeader(h);
    if (!n) return;
    for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
      if (aliases.includes(n) || n === field.replace(/_/g, ' ')) {
        if (map[field] === undefined) map[field] = i;
      }
    }
  });

  // Explicit business-contact layout (Customer name | Contact person | Mobile | Email)
  headers.forEach((h, i) => {
    const n = normHeader(h);
    if (!n) return;
    if (isCompanyHeader(n) && map.company_name === undefined) map.company_name = i;
    if ((n === 'contact person' || n === 'contact_person') && map.full_name === undefined) {
      map.full_name = i;
    }
  });

  // Fuzzy pass — person name columns only (never customer / company name)
  if (map.full_name === undefined) {
    headers.forEach((h, i) => {
      const n = normHeader(h);
      if (!n || map.full_name !== undefined) return;
      if (isPersonNameHeader(n) && !isPhoneHeader(n) && !isEmailHeader(n)) {
        map.full_name = i;
      }
    });
  }

  // Job-portal: column immediately left of Email is Name only when that header is a name column
  if (map.full_name === undefined && map.email !== undefined && map.email > 0) {
    const prev = normHeader(headers[map.email - 1] ?? '');
    if (isPersonNameHeader(prev) && !isPhoneHeader(prev)) {
      map.full_name = map.email - 1;
    }
  }

  // Never treat phone/email column as person name
  if (map.full_name !== undefined) {
    const hdr = normHeader(headers[map.full_name] ?? '');
    if (isPhoneHeader(hdr) || isEmailHeader(hdr) || map.full_name === map.phone) {
      delete map.full_name;
      headers.forEach((h, i) => {
        const n = normHeader(h);
        if (map.full_name !== undefined) return;
        if (n === 'contact person' || n === 'contact_person') map.full_name = i;
      });
    }
  }

  return map;
}

function buildColumnMapping(headers: string[], fieldIndex: Record<string, number>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, idx] of Object.entries(fieldIndex)) {
    if (headers[idx] !== undefined) out[headers[idx]] = field;
  }
  return out;
}

export function detectFormat(headers: string[]): DetectedFormat {
  const norm = headers.map(normHeader);
  const hasBusiness = norm.some(h => BUSINESS_HEADERS.has(h));
  const hasJobSeeker = norm.some(h => JOB_SEEKER_HEADERS.has(h));
  if (hasBusiness && !hasJobSeeker) return 'business_contact';
  if (hasJobSeeker && !hasBusiness) return 'job_seeker';
  if (hasBusiness && hasJobSeeker) return 'mixed';
  return 'unknown';
}

function parseDateLoose(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m) {
    const day = Number(m[1]);
    const month = Number(m[2]);
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    const d2 = new Date(year, month - 1, day);
    if (!Number.isNaN(d2.getTime())) return d2.toISOString().slice(0, 10);
  }
  return null;
}

function inferPersonKind(format: DetectedFormat, row: Partial<ParsedOutreachRow>): OutreachPersonKind {
  if (format === 'business_contact') return 'business_contact';
  if (format === 'job_seeker') return 'job_seeker';
  if (row.company_name && !row.functional_area) return 'business_contact';
  if (row.functional_area || row.specialization) return 'job_seeker';
  return 'unknown';
}

function mapDataRows(
  headers: string[],
  dataRows: string[][],
  detectedFormat: DetectedFormat,
): ParsedOutreachRow[] {
  const fieldIndex = buildHeaderMap(headers);
  const mappedFields = new Set(Object.keys(fieldIndex));
  const unmappedIdx = headers
    .map((h, i) => ({ h, i }))
    .filter(({ h, i }) => h.trim() && ![...mappedFields].some(f => fieldIndex[f] === i));

  const get = (row: string[], field: string): string => {
    const idx = fieldIndex[field];
    return idx === undefined ? '' : cellStr(row[idx]);
  };

  const results: ParsedOutreachRow[] = [];

  for (const row of dataRows) {
    if (!row.some(c => cellStr(c))) continue;

    const attributes: Record<string, string> = {};
    for (const { h, i } of unmappedIdx) {
      const v = cellStr(row[i]);
      if (v) attributes[h] = v;
    }
    for (let i = 0; i < headers.length; i++) {
      const h = headers[i]?.trim();
      const v = cellStr(row[i]);
      if (!v) continue;
      if (h) {
        attributes[h] = v;
      } else if (fieldIndex.full_name === i && !looksLikePhone(v) && !looksLikeEmail(v)) {
        attributes['Name'] = attributes['Name'] ?? v;
      }
    }

    let full_name = get(row, 'full_name') || null;
    if (full_name && (looksLikePhone(full_name) || looksLikeEmail(full_name))) {
      full_name = null;
    }
    if (!full_name) {
      full_name = attributes['Name'] || attributes['name'] || attributes['Contact person'] || attributes['Contact Person'] || null;
    }
    if (!full_name) {
      for (const [k, v] of Object.entries(attributes)) {
        const nk = normHeader(k);
        if ((nk === 'name' || nk === 'contact person') && v.trim() && !looksLikePhone(v) && !looksLikeEmail(v)) {
          full_name = v.trim();
          break;
        }
      }
    }

    const company_name = get(row, 'company_name') || attributes['Customer name'] || attributes['Customer Name'] || null;
    const emailRaw = get(row, 'email');
    const email = emailRaw ? emailRaw.toLowerCase() : null;
    let phone = get(row, 'phone') || null;

    const nameFromAttrs = attributes['Contact person'] || attributes['Contact Person']
      || attributes['Name'] || attributes['name'] || null;
    if (!full_name && nameFromAttrs && !looksLikePhone(nameFromAttrs)) {
      full_name = nameFromAttrs.trim();
    }
    if (!phone && full_name && looksLikePhone(full_name)) {
      phone = full_name;
      full_name = nameFromAttrs?.trim() && !looksLikePhone(nameFromAttrs) ? nameFromAttrs.trim() : null;
    }
    if (full_name && looksLikePhone(full_name)) {
      if (!phone) phone = full_name;
      full_name = nameFromAttrs?.trim() && !looksLikePhone(nameFromAttrs) ? nameFromAttrs.trim() : null;
    }

    const skillsRaw = get(row, 'skills');

    const partial: Partial<ParsedOutreachRow> = {
      full_name, company_name, email, phone,
      functional_area: get(row, 'functional_area') || null,
      specialization: get(row, 'specialization') || null,
    };

    if (!full_name && !email && !phone && !company_name) continue;

    results.push({
      full_name,
      company_name,
      person_kind: inferPersonKind(detectedFormat, partial),
      email,
      phone,
      date_of_birth: parseDateLoose(get(row, 'date_of_birth')),
      gender: get(row, 'gender') || null,
      address: get(row, 'address') || null,
      functional_area: get(row, 'functional_area') || null,
      specialization: get(row, 'specialization') || null,
      industry: get(row, 'industry') || null,
      current_salary: get(row, 'current_salary') || null,
      seniority_level: get(row, 'seniority_level') || null,
      location: get(row, 'location') || null,
      current_role: get(row, 'current_role') || null,
      skills: skillsRaw
        ? skillsRaw.split(/[;,|]/).map(s => s.trim()).filter(Boolean)
        : [],
      source_row_status: get(row, 'source_row_status') || null,
      source_followup_date: parseDateLoose(get(row, 'source_followup_date')),
      attributes,
    });
  }

  return results;
}

export function parseSheetFromGrid(
  sheetName: string,
  grid: string[][],
): ParsedSheet | null {
  if (grid.length < 2) return null;
  const headerIdx = findHeaderRowIndex(grid);
  const headers = (grid[headerIdx] ?? []).map((h) => cellStr(h));
  if (!headers.some(Boolean)) return null;
  const detectedFormat = detectFormat(headers);
  const fieldIndex = buildHeaderMap(headers);
  const rows = mapDataRows(headers, grid.slice(headerIdx + 1), detectedFormat);
  if (rows.length === 0) return null;
  return {
    sheetName,
    detectedFormat,
    columnMapping: buildColumnMapping(headers, fieldIndex),
    headers,
    rows,
  };
}

export function parseCsvFile(text: string, filename: string): ParsedImportFile {
  const grid = parseCsv(text.replace(/^\uFEFF/, ''));
  const sheet = parseSheetFromGrid('Sheet1', grid);
  return {
    filename,
    fileFormat: 'csv',
    sheets: sheet ? [sheet] : [],
  };
}

export function parseExcelBuffer(buffer: ArrayBuffer, filename: string): ParsedImportFile {
  const ext = filename.toLowerCase();
  const fileFormat: 'xlsx' | 'xls' = ext.endsWith('.xls') && !ext.endsWith('.xlsx') ? 'xls' : 'xlsx';
  const wb = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheets: ParsedSheet[] = [];

  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName];
    if (!ws) continue;
    const grid = XLSX.utils.sheet_to_json<string[]>(ws, {
      header: 1,
      defval: '',
      raw: false,
    }) as string[][];
    const parsed = parseSheetFromGrid(sheetName, grid);
    if (parsed) sheets.push(parsed);
  }

  return { filename, fileFormat, sheets };
}

export async function parseImportFile(file: File): Promise<ParsedImportFile> {
  const name = file.name;
  const lower = name.toLowerCase();

  if (lower.endsWith('.csv')) {
    const text = await file.text();
    return parseCsvFile(text, name);
  }

  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
    const buffer = await file.arrayBuffer();
    return parseExcelBuffer(buffer, name);
  }

  throw new Error('Unsupported file type. Upload .csv, .xlsx, or .xls');
}

export function downloadOutreachCsvTemplate(): void {
  const csv = [
    'Name,Email Id,Mobile No.,Functional Area,Current Location',
    'Jane Doe,jane@example.com,9876543210,IT,Bengaluru',
    '',
    'Customer name,Contact person,Mobile No,Email Id',
    'Acme Corp,John Smith,9876543211,john@acme.com',
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'outreach-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
